import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGaugeHigh,
  faUsers,
  faBoxOpen,
  faReceipt,
  faTags,
  faHeart,
  faCartShopping,
  faUserDoctor,
  faUser,
  faRightFromBracket,
  faPlus,
  faPenToSquare,
  faTrashCan,
  faXmark,
  faComments,
  faPaperPlane,
  faMoon,
  faSun,
  faImages,
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config/api.config';
import {
  customerPaymentVerifiedWaLink,
  customerShippedWaLink,
  customerDeliveredWaLink,
} from '../config/whatsapp.config';
import './AdminDashboard.css';

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: faGaugeHigh },
  { key: 'users', label: 'Users', icon: faUsers },
  { key: 'products', label: 'Products', icon: faBoxOpen },
  { key: 'orders', label: 'Orders', icon: faReceipt },
  { key: 'categories', label: 'Categories', icon: faTags },
  { key: 'messages', label: 'Messages', icon: faComments },
  { key: 'favourites', label: 'Favourites', icon: faHeart },
  { key: 'cart', label: 'Cart Items', icon: faCartShopping },
];

const emptyProduct = { id: null, name: '', categoryId: '', price: '', description: '' };
const emptyUser = { id: null, name: '', email: '', role: 'user', password: '', phone: '', details: '' };
const emptyCategory = { id: null, name: '' };

const ENTITY_ACTIONS = {
  dashboard: 'admin_dashboard',
  users: 'admin_users',
  products: 'admin_products',
  orders: 'admin_orders',
  categories: 'admin_categories',
  favourites: 'admin_favourites',
  cart: 'admin_cart',
  messages: 'admin_messages',
};

const AdminDashboard = ({ onLogout, user }) => {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favourites, setFavourites] = useState([]);
  const [cart_items, setCartItems] = useState([]);
  const [messages, setMessages] = useState([]);

  const [loading, setLoading] = useState(true);
  const [viewLoading, setViewLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('adminTheme') === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });
  const [toast, setToast] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [lightbox, setLightbox] = useState(null); // { src, title }

  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [imageData, setImageData] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [syncingImages, setSyncingImages] = useState(false);
  const [modalError, setModalError] = useState('');

  const fetchView = async (view, { silent = false } = {}) => {
    const action = ENTITY_ACTIONS[view] || 'admin_dashboard';
    if (!silent) setViewLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}?action=${action}`);
      const data = await response.json();
      if (data.status !== 'ok') {
        setError(data.message || 'Unable to load data.');
        return;
      }
      setError('');
      if (view === 'dashboard') {
        setStats(data.stats || null);
        setRecentOrders(data.recent_orders || []);
      } else if (view === 'users') setUsers(data.users || []);
      else if (view === 'products') setProducts(data.products || []);
      else if (view === 'orders') setOrders(data.orders || []);
      else if (view === 'categories') setCategories(data.categories || []);
      else if (view === 'favourites') setFavourites(data.favourites || []);
      else if (view === 'cart') setCartItems(data.cart_items || []);
      else if (view === 'messages') setMessages(data.messages || []);
    } catch (err) {
      console.error('Admin fetch error:', err);
      setError('Unable to load admin data.');
    } finally {
      setLoading(false);
      setViewLoading(false);
    }
  };

  useEffect(() => {
    fetchView(activeView);
    setSearchQuery('');
    // Product form needs category dropdown without waiting for modal open
    if (activeView === 'products') {
      fetchView('categories', { silent: true });
    }
  }, [activeView]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  const isDoctor = user?.role === 'doctor';
  const visibleMessages = useMemo(
    () => (isDoctor ? messages.filter((m) => Number(m.doctor_id) === Number(user?.id)) : messages),
    [messages, isDoctor, user]
  );

  const doctorCount = stats?.doctors ?? 0;
  const revenue = stats?.revenue ?? 0;

  const openLightbox = (src, title = 'Image') => {
    if (!src) return;
    setLightbox({ src, title });
  };

  const openProductImage = async (productId, title = 'Product') => {
    try {
      const response = await fetch(`${API_BASE_URL}?action=admin_product_image&productId=${productId}`);
      const data = await response.json();
      if (data.status === 'ok' && data.src) {
        openLightbox(data.src, title || data.name || 'Product');
      } else {
        setToast({ type: 'error', text: data.message || 'Image not found.' });
      }
    } catch (err) {
      setToast({ type: 'error', text: 'Failed to load product image.' });
    }
  };

  const openCategoryImage = async (categoryId, title = 'Category') => {
    try {
      const response = await fetch(`${API_BASE_URL}?action=admin_category_image&categoryId=${categoryId}`);
      const data = await response.json();
      if (data.status === 'ok' && data.src) {
        openLightbox(data.src, title || data.name || 'Category');
      } else {
        setToast({ type: 'error', text: data.message || 'Image not found.' });
      }
    } catch (err) {
      setToast({ type: 'error', text: 'Failed to load category image.' });
    }
  };

  const loadProductImagePreview = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}?action=admin_product_image&productId=${productId}`);
      const data = await response.json();
      if (data.status === 'ok' && data.src) {
        setImagePreview(data.src);
      }
    } catch (err) {
      /* preview is optional */
    }
  };

  const loadCategoryImagePreview = async (categoryId) => {
    try {
      const response = await fetch(`${API_BASE_URL}?action=admin_category_image&categoryId=${categoryId}`);
      const data = await response.json();
      if (data.status === 'ok' && data.src) {
        setImagePreview(data.src);
      }
    } catch (err) {
      /* preview is optional */
    }
  };

  const openPaymentProof = async (orderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}?action=admin_order_proof&orderId=${orderId}`);
      const data = await response.json();
      if (data.status === 'ok' && data.payment_proof) {
        openLightbox(data.payment_proof, `Payment proof #${orderId}`);
      } else {
        setToast({ type: 'error', text: data.message || 'Proof not found.' });
      }
    } catch (err) {
      setToast({ type: 'error', text: 'Failed to load payment proof.' });
    }
  };

  const handleReply = async (msg, body) => {
    if (!body.trim()) return false;
    try {
      const data = await postAction('send_message', {
        userId: msg.user_id,
        doctorId: msg.doctor_id,
        senderRole: 'doctor',
        body: body.trim(),
      });
      if (data.status === 'ok') {
        setToast({ type: 'success', text: 'Reply sent to patient.' });
        fetchView('messages', { silent: true });
        return true;
      }
      setToast({ type: 'error', text: data.message || 'Failed to send reply.' });
      return false;
    } catch (err) {
      console.error('Reply error:', err);
      setToast({ type: 'error', text: 'Network error while sending reply.' });
      return false;
    }
  };

  const handleOrderStatusChange = async (orderId, status) => {
    try {
      const data = await postAction('update_order_status', { orderId, status });
      if (data.status === 'ok') {
        setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
        if (data.whatsappCustomer) {
          window.open(data.whatsappCustomer, '_blank', 'noopener,noreferrer');
          setToast({
            type: 'success',
            text: `Order #${orderId} → ${status}. WhatsApp opened — tap Send to notify customer.`,
          });
        } else {
          setToast({ type: 'success', text: `Order #${orderId} updated to ${status}.` });
        }
      } else {
        setToast({ type: 'error', text: data.message || 'Failed to update order status.' });
      }
    } catch (err) {
      console.error('Order status error:', err);
      setToast({ type: 'error', text: 'Network error while updating order.' });
    }
  };

  const handleVerifyPayment = async (orderId, paymentStatus = 'paid') => {
    try {
      const data = await postAction('verify_payment', { orderId, paymentStatus });
      if (data.status === 'ok') {
        setOrders((prev) =>
          prev.map((o) => (o.id === orderId ? { ...o, payment_status: paymentStatus } : o))
        );
        if (data.whatsappCustomer) {
          window.open(data.whatsappCustomer, '_blank', 'noopener,noreferrer');
          setToast({
            type: 'success',
            text: `Order #${orderId} marked Paid. WhatsApp opened — tap Send to notify customer.`,
          });
        } else {
          setToast({
            type: 'success',
            text:
              paymentStatus === 'paid'
                ? `Order #${orderId} marked as Paid.`
                : `Order #${orderId} payment status updated.`,
          });
        }
      } else {
        setToast({ type: 'error', text: data.message || 'Failed to verify payment.' });
      }
    } catch (err) {
      console.error('Verify payment error:', err);
      setToast({ type: 'error', text: 'Network error while verifying payment.' });
    }
  };

  // ----- Modal helpers -----
  const openModal = (entity, mode, record) => {
    setModalError('');
    setImageData(null);
    setImagePreview('');
    if (entity === 'product') {
      setForm(
        mode === 'edit'
          ? {
              id: record.id,
              name: record.name || '',
              categoryId: record.category_id || record.categoryId || '',
              price: record.price != null ? String(record.price) : '',
              description: record.description || '',
            }
          : { ...emptyProduct }
      );
      if (mode === 'edit') {
        if (record.image_data) {
          setImagePreview(record.image_data);
        } else if (record.image && !String(record.image).startsWith('data:')) {
          setImagePreview(record.image);
        } else if (record.has_image || record.id) {
          loadProductImagePreview(record.id);
        }
      }
    } else if (entity === 'user') {
      setForm(
        mode === 'edit'
          ? {
              id: record.id,
              name: record.name || '',
              email: record.email || '',
              role: record.role || 'user',
              password: '',
              phone: record.phone || '',
              details: record.details || '',
            }
          : { ...emptyUser }
      );
    } else if (entity === 'category') {
      setForm(mode === 'edit' ? { id: record.id, name: record.name || '' } : { ...emptyCategory });
      if (mode === 'edit') {
        if (record.image && !String(record.image).startsWith('data:')) {
          setImagePreview(record.image);
        } else if (record.has_image || record.id) {
          loadCategoryImagePreview(record.id);
        }
      }
    }
    setModal({ entity, mode });
  };

  const closeModal = () => {
    setModal(null);
    setForm({});
    setModalError('');
    setImageData(null);
    setImagePreview('');
  };

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImage = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setModalError('Please select a valid image file.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setModalError('Image must be under 4MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageData(reader.result);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const postAction = async (action, body) => {
    const response = await fetch(`${API_BASE_URL}?action=${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  };

  const submitModal = async (e) => {
    e.preventDefault();
    setModalError('');
    const { entity, mode } = modal;

    // Client-side validation
    if (entity === 'product' && (!form.name.trim() || !form.categoryId)) {
      setModalError('Product name and category are required.');
      return;
    }
    if (entity === 'user' && (!form.name.trim() || !form.email.trim() || (mode === 'add' && !form.password))) {
      setModalError('Name, email and password are required.');
      return;
    }
    if (entity === 'category' && !form.name.trim()) {
      setModalError('Category name is required.');
      return;
    }

    setSubmitting(true);
    try {
      let action;
      let body;
      if (entity === 'product') {
        action = mode === 'add' ? 'add_product' : 'update_product';
        body = {
          id: form.id,
          name: form.name.trim(),
          categoryId: Number(form.categoryId),
          price: parseFloat(form.price) || 0,
          description: (form.description || '').trim(),
        };
        if (mode === 'add') body.imageData = imageData || '';
        else if (imageData) body.imageData = imageData; // only send when changed
      } else if (entity === 'user') {
        action = mode === 'add' ? 'add_user' : 'update_user';
        body = {
          id: form.id,
          name: form.name.trim(),
          email: form.email.trim(),
          role: form.role,
          phone: (form.phone || '').trim(),
          details: (form.details || '').trim(),
        };
        if (form.password) body.password = form.password;
      } else {
        action = mode === 'add' ? 'add_category' : 'update_category';
        body = { id: form.id, name: form.name.trim() };
        if (mode === 'add') body.imageData = imageData || '';
        else if (imageData) body.imageData = imageData;
      }

      const data = await postAction(action, body);
      if (data.status === 'ok') {
        setToast({ type: 'success', text: `${label(entity)} ${mode === 'add' ? 'added' : 'updated'} successfully.` });
        closeModal();

        // Edit: patch local state so the table stays on the current page with no full refresh.
        // Add: refetch so the new row (server id) appears.
        if (mode === 'edit') {
          if (entity === 'product') {
            const catId = Number(form.categoryId);
            const catName = categories.find((c) => Number(c.id) === catId)?.name;
            setProducts((prev) =>
              prev.map((p) =>
                Number(p.id) === Number(form.id)
                  ? {
                      ...p,
                      name: form.name.trim(),
                      category_id: catId,
                      category_name: catName || p.category_name,
                      price: parseFloat(form.price) || 0,
                      description: (form.description || '').trim(),
                      ...(imageData
                        ? { has_image: true, image: null, thumb_src: imageData }
                        : {}),
                    }
                  : p
              )
            );
            fetchView('categories', { silent: true });
          } else if (entity === 'user') {
            setUsers((prev) =>
              prev.map((u) =>
                Number(u.id) === Number(form.id)
                  ? {
                      ...u,
                      name: form.name.trim(),
                      email: form.email.trim(),
                      role: form.role,
                      phone: (form.phone || '').trim(),
                      details: (form.details || '').trim(),
                    }
                  : u
              )
            );
          } else {
            const nextName = form.name.trim();
            setCategories((prev) =>
              prev.map((c) =>
                Number(c.id) === Number(form.id)
                  ? {
                      ...c,
                      name: nextName,
                      ...(imageData
                        ? { has_image: true, image: null, thumb_src: imageData }
                        : {}),
                    }
                  : c
              )
            );
            setProducts((prev) =>
              prev.map((p) =>
                Number(p.category_id) === Number(form.id) ? { ...p, category_name: nextName } : p
              )
            );
          }
        } else {
          const viewKey = entity === 'user' ? 'users' : entity === 'product' ? 'products' : 'categories';
          fetchView(viewKey, { silent: true });
          if (entity === 'product') fetchView('categories', { silent: true });
        }
      } else {
        setModalError(data.message || 'Something went wrong.');
      }
    } catch (err) {
      console.error('Save error:', err);
      setModalError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entity, record) => {
    const name = record.name || `#${record.id}`;
    if (!window.confirm(`Delete ${label(entity).toLowerCase()} "${name}"? This cannot be undone.`)) return;
    try {
      const action = { product: 'delete_product', user: 'delete_user', category: 'delete_category' }[entity];
      const data = await postAction(action, { id: record.id });
      if (data.status === 'ok') {
        setToast({ type: 'success', text: `${label(entity)} deleted.` });
        const viewKey = entity === 'user' ? 'users' : entity === 'product' ? 'products' : 'categories';
        fetchView(viewKey, { silent: true });
        if (entity === 'product' || entity === 'category') fetchView('categories', { silent: true });
      } else {
        setToast({ type: 'error', text: data.message || 'Failed to delete.' });
      }
    } catch (err) {
      console.error('Delete error:', err);
      setToast({ type: 'error', text: 'Network error while deleting.' });
    }
  };

  const handleSyncMedicineImages = async () => {
    if (
      !window.confirm(
        'Match product names to JPG files in the medicine images folder and link them in the database?\n\nThis will replace any previously uploaded product images when a matching file is found.'
      )
    ) {
      return;
    }
    setSyncingImages(true);
    try {
      const data = await postAction('sync_medicine_images', {});
      if (data.status === 'ok') {
        const matched = data.matched ?? 0;
        const skipped = data.skipped ?? 0;
        const missing = data.missing ?? 0;
        setToast({
          type: 'success',
          text: `Images synced: ${matched} linked, ${skipped} skipped, ${missing} no matching file.`,
        });
        fetchView('products', { silent: true });
      } else {
        setToast({ type: 'error', text: data.message || 'Failed to sync medicine images.' });
      }
    } catch (err) {
      console.error('Sync images error:', err);
      setToast({ type: 'error', text: 'Network error while syncing images.' });
    } finally {
      setSyncingImages(false);
    }
  };

  const label = (entity) => ({ product: 'Product', user: 'User', category: 'Category' }[entity] || entity);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem('adminTheme', next);
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // ----------------- RENDER -----------------
  if (loading) {
    return (
      <div className="admin-shell" data-theme={theme}>
        <div className="admin-fullscreen-msg">Loading admin dashboard…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-shell" data-theme={theme}>
        <div className="admin-fullscreen-msg error">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-shell" data-theme={theme}>
      {/* ===== Top bar ===== */}
      <header className="admin-topbar">
        <button
          type="button"
          className="admin-menu-toggle"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <span />
          <span />
          <span />
        </button>

        <div className="admin-brand">
          <img src="/images/logo.jpeg" alt="AlamCareRx" className="admin-brand-logo" />
          <span className="admin-brand-name">AlamCareRx</span>
          <span className="admin-brand-tag">{isDoctor ? 'Doctor' : 'Admin'}</span>
        </div>

        <div className="admin-topbar-right">
          <button
            type="button"
            className="admin-theme-btn"
            onClick={toggleTheme}
            title={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
            aria-label="Toggle theme"
          >
            <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
          </button>
          <div className="admin-profile">
            <button
              type="button"
              className="admin-profile-btn"
              onClick={() => setProfileOpen((v) => !v)}
              onBlur={() => setTimeout(() => setProfileOpen(false), 150)}
              title="Account"
            >
              <FontAwesomeIcon icon={faUser} />
            </button>
            {profileOpen && (
              <div className="admin-profile-menu">
                <div className="admin-profile-head">
                  <FontAwesomeIcon icon={faUser} className="admin-profile-avatar" />
                  <div>
                    <p className="admin-profile-name">{user?.name || 'Administrator'}</p>
                    <p className="admin-profile-email">{user?.email || 'admin'}</p>
                  </div>
                </div>
                <button type="button" className="admin-profile-logout" onClick={onLogout}>
                  <FontAwesomeIcon icon={faRightFromBracket} /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="admin-body">
        {/* ===== Sidebar ===== */}
        <aside className={`admin-sidebar${sidebarOpen ? ' open' : ''}`}>
          <nav>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`admin-nav-item${activeView === item.key ? ' active' : ''}`}
                onClick={() => {
                  setActiveView(item.key);
                  setSidebarOpen(false);
                }}
              >
                <FontAwesomeIcon icon={item.icon} className="admin-nav-icon" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {sidebarOpen && <div className="admin-backdrop" onClick={() => setSidebarOpen(false)} />}

        {/* ===== Main content ===== */}
        <main className="admin-content">
          {viewLoading && (
            <div className="admin-view-loading">Loading…</div>
          )}

          {activeView === 'dashboard' &&
            renderDashboard({ stats, recentOrders, revenue, doctorCount })}

          {activeView === 'users' &&
            renderUsers({
              users,
              searchQuery,
              onSearch: setSearchQuery,
              onAdd: () => openModal('user', 'add'),
              onEdit: (u) => openModal('user', 'edit', u),
              onDelete: (u) => handleDelete('user', u),
            })}

          {activeView === 'products' &&
            renderProducts({
              products,
              searchQuery,
              onSearch: setSearchQuery,
              onAdd: async () => {
                if (!categories.length) await fetchView('categories', { silent: true });
                openModal('product', 'add');
              },
              onEdit: async (p) => {
                if (!categories.length) await fetchView('categories', { silent: true });
                openModal('product', 'edit', p);
              },
              onDelete: (p) => handleDelete('product', p),
              onViewImage: openProductImage,
              onSyncImages: handleSyncMedicineImages,
              syncingImages,
            })}

          {activeView === 'orders' &&
            renderOrders({
              orders,
              searchQuery,
              onSearch: setSearchQuery,
              onStatusChange: handleOrderStatusChange,
              onVerifyPayment: handleVerifyPayment,
              onViewProof: openPaymentProof,
            })}

          {activeView === 'messages' && (
            <MessagesView
              messages={visibleMessages}
              isDoctor={isDoctor}
              onReply={handleReply}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
            />
          )}

          {activeView === 'categories' &&
            renderCategories({
              categories,
              searchQuery,
              onSearch: setSearchQuery,
              onAdd: () => openModal('category', 'add'),
              onEdit: (c) => openModal('category', 'edit', c),
              onDelete: (c) => handleDelete('category', c),
              onViewImage: openCategoryImage,
            })}

          {activeView === 'favourites' &&
            renderFavourites({ favourites, searchQuery, onSearch: setSearchQuery })}

          {activeView === 'cart' &&
            renderCart({ cart_items, searchQuery, onSearch: setSearchQuery })}
        </main>
      </div>

      {/* ===== Modal ===== */}
      {modal && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-head">
              <h3>
                {modal.mode === 'add' ? 'Add' : 'Edit'} {label(modal.entity)}
              </h3>
              <button type="button" className="admin-modal-close" onClick={closeModal}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <form className="admin-form" onSubmit={submitModal}>
              {modal.entity === 'product' && (
                <>
                  <div className="admin-form-grid">
                    <div className="admin-field">
                      <label>Product Name *</label>
                      <input name="name" value={form.name} onChange={handleField} placeholder="e.g. Paracetamol 500mg" required />
                    </div>
                    <div className="admin-field">
                      <label>Category *</label>
                      <select name="categoryId" value={form.categoryId} onChange={handleField} required>
                        <option value="">Select category</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="admin-field">
                      <label>Price (Rs.)</label>
                      <input name="price" type="number" min="0" step="0.01" value={form.price} onChange={handleField} placeholder="0.00" />
                    </div>
                    <div className="admin-field">
                      <label>Product Picture</label>
                      <input type="file" accept="image/*" onChange={handleImage} />
                    </div>
                    <div className="admin-field admin-field-full">
                      <label>Description</label>
                      <textarea name="description" rows="3" value={form.description} onChange={handleField} placeholder="Short product description" />
                    </div>
                  </div>
                  {imagePreview && (
                    <div className="admin-image-preview">
                      <img src={imagePreview} alt="Preview" />
                      <span>{imageData ? 'New image ready to upload' : 'Current image'}</span>
                    </div>
                  )}
                </>
              )}

              {modal.entity === 'user' && (
                <div className="admin-form-grid">
                  <div className="admin-field">
                    <label>Full Name *</label>
                    <input name="name" value={form.name} onChange={handleField} placeholder="e.g. John Doe" required />
                  </div>
                  <div className="admin-field">
                    <label>Email *</label>
                    <input name="email" type="email" value={form.email} onChange={handleField} placeholder="name@example.com" required />
                  </div>
                  <div className="admin-field">
                    <label>Role</label>
                    <select name="role" value={form.role} onChange={handleField}>
                      <option value="user">User</option>
                      <option value="doctor">Doctor</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="admin-field">
                    <label>Phone</label>
                    <input
                      name="phone"
                      value={form.phone || ''}
                      onChange={handleField}
                      placeholder="+92 343 5447588"
                    />
                  </div>
                  <div className="admin-field admin-field-full">
                    <label>Details {form.role === 'doctor' ? '(shown on About / Consult)' : ''}</label>
                    <textarea
                      name="details"
                      rows="4"
                      value={form.details || ''}
                      onChange={handleField}
                      placeholder="Doctor bio / professional details (one line per detail)"
                    />
                  </div>
                  <div className="admin-field">
                    <label>{modal.mode === 'add' ? 'Password *' : 'New Password'}</label>
                    <input
                      name="password"
                      type="password"
                      value={form.password}
                      onChange={handleField}
                      placeholder={modal.mode === 'add' ? 'Set a password' : 'Leave blank to keep current'}
                    />
                  </div>
                </div>
              )}

              {modal.entity === 'category' && (
                <>
                  <div className="admin-form-grid">
                    <div className="admin-field">
                      <label>Category Name *</label>
                      <input
                        name="name"
                        value={form.name}
                        onChange={handleField}
                        placeholder="e.g. Supplements"
                        required
                      />
                    </div>
                    <div className="admin-field">
                      <label>Category Picture</label>
                      <input type="file" accept="image/*" onChange={handleImage} />
                    </div>
                    <div className="admin-field admin-field-full">
                      <p className="admin-field-hint">
                        Upload an image for this category. It will be shown on the home page category carousel.
                      </p>
                    </div>
                  </div>
                  {imagePreview && (
                    <div className="admin-image-preview">
                      <img src={imagePreview} alt="Category preview" />
                      <span>{imageData ? 'New image ready to upload' : 'Current category image'}</span>
                    </div>
                  )}
                </>
              )}

              {modalError && <div className="admin-form-error">{modalError}</div>}

              <div className="admin-modal-actions">
                <button type="button" className="admin-btn ghost" onClick={closeModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn primary" disabled={submitting}>
                  {submitting ? 'Saving…' : modal.mode === 'add' ? 'Create' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== Toast ===== */}
      {toast && <div className={`admin-toast ${toast.type}`}>{toast.text}</div>}

      {lightbox && (
        <div className="admin-lightbox" onClick={() => setLightbox(null)} role="dialog" aria-modal="true">
          <div className="admin-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <div className="admin-lightbox-head">
              <span>{lightbox.title}</span>
              <button type="button" className="admin-modal-close" onClick={() => setLightbox(null)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <img src={lightbox.src} alt={lightbox.title} className="admin-lightbox-img" />
          </div>
        </div>
      )}
    </div>
  );
};

// ================= View renderers =================

function renderDashboard({ stats, recentOrders, revenue, doctorCount }) {
  const s = stats || {};
  const cards = [
    { label: 'Total Users', value: s.users || 0, icon: faUsers, tone: 'blue' },
    { label: 'Doctors', value: doctorCount || s.doctors || 0, icon: faUserDoctor, tone: 'violet' },
    { label: 'Products', value: s.products || 0, icon: faBoxOpen, tone: 'green' },
    { label: 'Orders', value: s.orders || 0, icon: faReceipt, tone: 'amber' },
  ];

  const barData = [
    { name: 'Users', value: s.users || 0, color: '#3b82f6' },
    { name: 'Doctors', value: s.doctors || 0, color: '#8b5cf6' },
    { name: 'Products', value: s.products || 0, color: '#22c55e' },
    { name: 'Orders', value: s.orders || 0, color: '#f59e0b' },
    { name: 'Categories', value: s.categories || 0, color: '#14b8a6' },
    { name: 'Favourites', value: s.favourites || 0, color: '#ef4444' },
    { name: 'Cart', value: s.cart_items || 0, color: '#ec4899' },
  ];

  const statusCounts = (recentOrders || []).reduce((acc, o) => {
    const st = (o.status || 'placed').toLowerCase();
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});
  const statusColors = {
    placed: '#3b82f6',
    pending: '#f59e0b',
    shipped: '#8b5cf6',
    delivered: '#22c55e',
    completed: '#22c55e',
  };
  const donutData = Object.entries(statusCounts).map(([name, value], i) => ({
    name,
    value,
    color: statusColors[name] || ['#0ea5e9', '#a855f7', '#f43f5e', '#84cc16'][i % 4],
  }));
  const rev = Number(revenue || s.revenue || 0);

  return (
    <div className="admin-view">
      <div className="admin-view-head">
        <div>
          <h1>Dashboard</h1>
          <p>Overview of your store (lightweight — counts only).</p>
        </div>
      </div>

      <div className="admin-cards">
        {cards.map((c) => (
          <div key={c.label} className={`admin-card tone-${c.tone}`}>
            <div className="admin-card-icon">
              <FontAwesomeIcon icon={c.icon} />
            </div>
            <div className="admin-card-body">
              <span className="admin-card-value">{c.value}</span>
              <span className="admin-card-label">{c.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="admin-charts">
        <div className="admin-chart-card">
          <div className="admin-chart-head">
            <h3>Store Overview</h3>
            <span className="admin-chart-sub">Record counts</span>
          </div>
          <BarChart data={barData} />
        </div>

        <div className="admin-chart-card">
          <div className="admin-chart-head">
            <h3>Recent orders</h3>
            <span className="admin-chart-sub">Total revenue: Rs. {rev.toFixed(2)}</span>
          </div>
          {donutData.length ? (
            <DonutChart data={donutData} total={(recentOrders || []).length} />
          ) : (
            <div className="admin-empty">No recent orders.</div>
          )}
          {(recentOrders || []).length > 0 && (
            <ul className="admin-recent-orders">
              {recentOrders.map((o) => (
                <li key={o.id}>
                  <strong>#{o.id}</strong> {o.user_name} — Rs. {parseFloat(o.total).toFixed(2)}{' '}
                  <span className="admin-subtle">{o.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionHead({ title, subtitle, onAdd, addLabel, actions }) {
  return (
    <div className="admin-view-head">
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {(onAdd || actions) && (
        <div className="admin-view-head-actions">
          {actions}
          {onAdd && (
            <button type="button" className="admin-btn primary" onClick={onAdd}>
              <FontAwesomeIcon icon={faPlus} /> {addLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function RowActions({ onEdit, onDelete }) {
  return (
    <div className="admin-row-actions">
      <button type="button" className="admin-icon-btn edit" onClick={onEdit} title="Edit">
        <FontAwesomeIcon icon={faPenToSquare} />
      </button>
      <button type="button" className="admin-icon-btn delete" onClick={onDelete} title="Delete">
        <FontAwesomeIcon icon={faTrashCan} />
      </button>
    </div>
  );
}

function TableSearch({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div className="admin-table-search">
      <input
        type="search"
        value={value || ''}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        aria-label="Search table"
      />
    </div>
  );
}

function matchesSearch(row, query, fields) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => String(row[f] ?? '').toLowerCase().includes(q));
}

function TableCard({ children, empty, emptyText }) {
  return (
    <div className="admin-table-card">
      <div className="admin-table-scroll">
        {empty ? <div className="admin-empty">{emptyText}</div> : <table className="admin-table">{children}</table>}
      </div>
    </div>
  );
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function DataTable({
  data,
  searchQuery,
  onSearch,
  searchPlaceholder = 'Search…',
  searchFields,
  customFilter,
  emptyText = 'No matching records.',
  emptyNoDataText = 'No records found.',
  headers,
  renderRow,
  defaultPageSize = 25,
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const filtered = useMemo(() => {
    const rows = data || [];
    if (customFilter) return rows.filter((row) => customFilter(row, searchQuery));
    if (searchFields) return rows.filter((row) => matchesSearch(row, searchQuery, searchFields));
    return rows;
  }, [data, searchQuery, searchFields, customFilter]);

  // Only reset page when search or page size changes — not when row data is patched/refetched.
  useEffect(() => {
    setPage(1);
  }, [searchQuery, pageSize]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIdx = total === 0 ? 0 : (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, total);
  const pageRows = filtered.slice(startIdx, endIdx);

  const pageButtons = useMemo(() => {
    const buttons = [];
    const maxButtons = 5;
    let from = Math.max(1, safePage - 2);
    let to = Math.min(totalPages, from + maxButtons - 1);
    from = Math.max(1, to - maxButtons + 1);
    for (let i = from; i <= to; i += 1) buttons.push(i);
    return buttons;
  }, [safePage, totalPages]);

  return (
    <>
      <div className="admin-dt-toolbar">
        <label className="admin-dt-length">
          Show{' '}
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>{' '}
          entries
        </label>
        <TableSearch value={searchQuery} onChange={onSearch} placeholder={searchPlaceholder} />
      </div>
      <TableCard
        empty={pageRows.length === 0}
        emptyText={(data || []).length ? emptyText : emptyNoDataText}
      >
        <thead>{headers}</thead>
        <tbody>{pageRows.map((row, i) => renderRow(row, startIdx + i))}</tbody>
      </TableCard>
      <div className="admin-dt-footer">
        <div className="admin-dt-info">
          {total === 0
            ? 'Showing 0 entries'
            : `Showing ${startIdx + 1} to ${endIdx} of ${total} entries`}
        </div>
        <div className="admin-dt-paginate" role="navigation" aria-label="Table pagination">
          <button type="button" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
            Previous
          </button>
          {pageButtons.map((p) => (
            <button
              key={p}
              type="button"
              className={p === safePage ? 'active' : ''}
              onClick={() => setPage(p)}
              aria-current={p === safePage ? 'page' : undefined}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            disabled={safePage >= totalPages}
            onClick={() => setPage(safePage + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}

function renderUsers({ users, searchQuery, onSearch, onAdd, onEdit, onDelete }) {
  return (
    <div className="admin-view">
      <SectionHead title="Users" subtitle="Manage customer and admin accounts." onAdd={onAdd} addLabel="Add User" />
      <DataTable
        data={users}
        searchQuery={searchQuery}
        onSearch={onSearch}
        searchPlaceholder="Search users by name, email, phone, role…"
        searchFields={['name', 'email', 'phone', 'role', 'details']}
        emptyText="No matching users."
        emptyNoDataText="No users found."
        headers={
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Role</th>
            <th>Created</th>
            <th className="col-actions">Actions</th>
          </tr>
        }
        renderRow={(u) => (
          <tr key={u.id}>
            <td>
              <div>{u.name}</div>
              {u.role === 'doctor' && u.details && (
                <div className="admin-user-details-preview">{u.details}</div>
              )}
            </td>
            <td>{u.email}</td>
            <td>{u.phone || '—'}</td>
            <td>
              <span className={`admin-badge ${u.role}`}>{u.role}</span>
            </td>
            <td>{new Date(u.created_at).toLocaleDateString()}</td>
            <td className="col-actions">
              <RowActions onEdit={() => onEdit(u)} onDelete={() => onDelete(u)} />
            </td>
          </tr>
        )}
      />
    </div>
  );
}

function ProductThumb({ product, onViewImage }) {
  const pathThumb =
    product.image && !String(product.image).startsWith('data:') ? product.image : null;
  // Prefer upload (image_data) over synced file path — same order as preview
  const preferUpload = Boolean(product.has_image);
  const [src, setSrc] = useState(
    () => product.thumb_src || (!preferUpload ? pathThumb : null)
  );

  useEffect(() => {
    if (product.thumb_src) {
      setSrc(product.thumb_src);
      return undefined;
    }
    if (!preferUpload) {
      setSrc(pathThumb);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}?action=admin_product_image&productId=${product.id}`
        );
        const data = await response.json();
        if (!cancelled && data.status === 'ok' && data.src) {
          setSrc(data.src);
        }
      } catch (err) {
        /* keep placeholder */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [product.id, product.has_image, product.thumb_src, product.image, preferUpload, pathThumb]);

  const hasImg = preferUpload || pathThumb || src;
  if (!hasImg) {
    return <span className="admin-no-thumb">—</span>;
  }

  return (
    <button
      type="button"
      className="admin-thumb-btn"
      onClick={() => onViewImage && onViewImage(product.id, product.name)}
      title="View image"
    >
      {src ? (
        <img className="admin-thumb" src={src} alt={product.name} />
      ) : (
        <span className="admin-thumb-placeholder">View</span>
      )}
    </button>
  );
}

function renderProducts({
  products,
  searchQuery,
  onSearch,
  onAdd,
  onEdit,
  onDelete,
  onViewImage,
  onSyncImages,
  syncingImages,
}) {
  return (
    <div className="admin-view">
      <SectionHead
        title="Products"
        subtitle="Add products with category, name, price and image — or sync pack shots from the medicine images folder by matching product names."
        onAdd={onAdd}
        addLabel="Add Product"
        actions={
          onSyncImages ? (
            <button
              type="button"
              className="admin-btn ghost"
              onClick={onSyncImages}
              disabled={syncingImages}
              title="Link JPG files from medicine_images folder to products with the same name"
            >
              <FontAwesomeIcon icon={faImages} />
              {syncingImages ? 'Syncing…' : 'Sync medicine images'}
            </button>
          ) : null
        }
      />
      <DataTable
        data={products}
        searchQuery={searchQuery}
        onSearch={onSearch}
        searchPlaceholder="Search products by name, category, price…"
        searchFields={['name', 'category_name', 'description', 'price']}
        emptyText="No matching products."
        emptyNoDataText="No products found."
        defaultPageSize={25}
        headers={
          <tr>
            <th>Image</th>
            <th>Name</th>
            <th>Category</th>
            <th>Price</th>
            <th className="col-actions">Actions</th>
          </tr>
        }
        renderRow={(p) => (
          <tr key={p.id}>
            <td>
              <ProductThumb product={p} onViewImage={onViewImage} />
            </td>
            <td>{p.name}</td>
            <td>{p.category_name || 'Uncategorized'}</td>
            <td>Rs. {parseFloat(p.price).toFixed(2)}</td>
            <td className="col-actions">
              <RowActions onEdit={() => onEdit(p)} onDelete={() => onDelete(p)} />
            </td>
          </tr>
        )}
      />
    </div>
  );
}

function CategoryThumb({ category, onViewImage }) {
  const pathThumb =
    category.image && !String(category.image).startsWith('data:') ? category.image : null;
  const preferUpload = Boolean(category.has_image);
  const [src, setSrc] = useState(
    () => category.thumb_src || (!preferUpload ? pathThumb : null)
  );

  useEffect(() => {
    if (category.thumb_src) {
      setSrc(category.thumb_src);
      return undefined;
    }
    if (!preferUpload) {
      setSrc(pathThumb);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}?action=admin_category_image&categoryId=${category.id}`
        );
        const data = await response.json();
        if (!cancelled && data.status === 'ok' && data.src) {
          setSrc(data.src);
        }
      } catch (err) {
        /* keep placeholder */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [category.id, category.has_image, category.thumb_src, category.image, preferUpload, pathThumb]);

  const hasImg = preferUpload || pathThumb || src;
  if (!hasImg) {
    return <span className="admin-no-thumb">—</span>;
  }

  return (
    <button
      type="button"
      className="admin-thumb-btn"
      onClick={() => onViewImage && onViewImage(category.id, category.name)}
      title="View image"
    >
      {src ? (
        <img className="admin-thumb" src={src} alt={category.name} />
      ) : (
        <span className="admin-thumb-placeholder">View</span>
      )}
    </button>
  );
}

function renderCategories({ categories, searchQuery, onSearch, onAdd, onEdit, onDelete, onViewImage }) {
  return (
    <div className="admin-view">
      <SectionHead title="Categories" subtitle="Organize products into categories and upload category images." onAdd={onAdd} addLabel="Add Category" />
      <DataTable
        data={categories}
        searchQuery={searchQuery}
        onSearch={onSearch}
        searchPlaceholder="Search categories…"
        searchFields={['name', 'product_count']}
        emptyText="No matching categories."
        emptyNoDataText="No categories found."
        defaultPageSize={25}
        headers={
          <tr>
            <th>Image</th>
            <th>Name</th>
            <th>Products</th>
            <th>Created</th>
            <th className="col-actions">Actions</th>
          </tr>
        }
        renderRow={(c) => (
          <tr key={c.id}>
            <td>
              <CategoryThumb category={c} onViewImage={onViewImage} />
            </td>
            <td>{c.name}</td>
            <td>{c.product_count != null ? c.product_count : 0}</td>
            <td>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
            <td className="col-actions">
              <RowActions onEdit={() => onEdit(c)} onDelete={() => onDelete(c)} />
            </td>
          </tr>
        )}
      />
    </div>
  );
}

function orderMatchesSearch(o, searchQuery) {
  const q = (searchQuery || '').trim().toLowerCase();
  if (!q) return true;
  const hay = [
    o.id,
    o.user_name,
    o.user_email,
    o.phone,
    o.status,
    o.payment_method,
    o.payment_status,
    o.shipping_method,
    o.total,
    ...(o.items || []).map((i) => i.name),
  ]
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

function renderOrders({ orders, searchQuery, onSearch, onStatusChange, onVerifyPayment, onViewProof }) {
  return (
    <div className="admin-view">
      <SectionHead
        title="Orders"
        subtitle="Update fulfilment. Click proof thumbnail to view. WhatsApp = free Click-to-Chat."
      />
      <DataTable
        data={orders}
        searchQuery={searchQuery}
        onSearch={onSearch}
        searchPlaceholder="Search orders by ID, customer, phone, status, items…"
        customFilter={orderMatchesSearch}
        emptyText="No matching orders."
        emptyNoDataText="No orders yet."
        defaultPageSize={25}
        headers={
          <tr>
            <th>Order ID</th>
            <th>User</th>
            <th>Items</th>
            <th>Total</th>
            <th>Payment</th>
            <th>Proof</th>
            <th>Shipping</th>
            <th>Status</th>
            <th>WhatsApp</th>
            <th>Created</th>
          </tr>
        }
        renderRow={(o) => {
          const payLabels = {
            cod: 'Cash on Delivery',
            online: 'Online Payment',
            easypaisa: 'Online (EasyPaisa)',
            jazzcash: 'Online (JazzCash)',
          };
          const payLabel = payLabels[o.payment_method] || o.payment_method || 'COD';
          const shipLabel = o.shipping_method === 'pickup' ? 'Store pickup' : 'Home delivery';
          const payStatus = (o.payment_status || 'unpaid').toLowerCase();
          const paid = payStatus === 'paid';
          const pendingVerify = payStatus === 'pending_verification';
          const status = (o.status || 'placed').toLowerCase();
          const waPaid = customerPaymentVerifiedWaLink(o);
          const waShipped = customerShippedWaLink(o);
          const waDelivered = customerDeliveredWaLink(o);
          const hasProof = o.has_payment_proof || o.payment_proof;
          return (
            <tr key={o.id}>
              <td>{o.id}</td>
              <td>
                {o.user_name}
                <div className="admin-subtle">{o.user_email}</div>
                {o.phone ? <div className="admin-subtle">{o.phone}</div> : null}
              </td>
              <td>{(o.items || []).map((i) => `${i.name} x${i.quantity}`).join(', ') || '—'}</td>
              <td>Rs. {parseFloat(o.total).toFixed(2)}</td>
              <td>
                {payLabel}
                {paid ? (
                  <div className="admin-subtle admin-paid-tag">Paid</div>
                ) : pendingVerify ? (
                  <div className="admin-subtle admin-pending-tag">Awaiting verification</div>
                ) : o.payment_method === 'cod' ? (
                  <div className="admin-subtle">Unpaid (COD)</div>
                ) : (
                  <div className="admin-subtle">Unpaid</div>
                )}
                {pendingVerify ? (
                  <button
                    type="button"
                    className="admin-verify-btn"
                    onClick={() => onVerifyPayment && onVerifyPayment(o.id, 'paid')}
                  >
                    Mark Paid
                  </button>
                ) : null}
              </td>
              <td>
                {hasProof ? (
                  <button
                    type="button"
                    className="admin-proof-view-btn"
                    onClick={() => onViewProof && onViewProof(o.id)}
                    title="View payment proof"
                  >
                    {o.payment_proof ? (
                      <img src={o.payment_proof} alt={`Proof #${o.id}`} className="admin-proof-thumb" />
                    ) : (
                      <span>View</span>
                    )}
                  </button>
                ) : (
                  <span className="admin-subtle">—</span>
                )}
              </td>
              <td>{shipLabel}</td>
              <td>
                <select
                  className={`admin-status-select admin-status ${status}`}
                  value={['placed', 'shipped', 'delivered'].includes(status) ? status : 'placed'}
                  onChange={(e) => onStatusChange && onStatusChange(o.id, e.target.value)}
                >
                  <option value="placed">Placed</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                </select>
              </td>
              <td>
                <div className="admin-wa-actions">
                  {waPaid ? (
                    <a className="btn-whatsapp-sm" href={waPaid} target="_blank" rel="noopener noreferrer">
                      Paid msg
                    </a>
                  ) : null}
                  {waShipped ? (
                    <a className="btn-whatsapp-sm" href={waShipped} target="_blank" rel="noopener noreferrer">
                      Shipped
                    </a>
                  ) : null}
                  {waDelivered ? (
                    <a className="btn-whatsapp-sm" href={waDelivered} target="_blank" rel="noopener noreferrer">
                      Delivered
                    </a>
                  ) : null}
                  {!waPaid && !waShipped && !waDelivered ? (
                    <span className="admin-subtle">No phone</span>
                  ) : null}
                </div>
              </td>
              <td>{new Date(o.created_at).toLocaleString()}</td>
            </tr>
          );
        }}
      />
    </div>
  );
}

function renderFavourites({ favourites, searchQuery, onSearch }) {
  return (
    <div className="admin-view">
      <SectionHead title="Favourites" subtitle="Products customers have favourited." />
      <DataTable
        data={favourites}
        searchQuery={searchQuery}
        onSearch={onSearch}
        searchPlaceholder="Search favourites…"
        searchFields={['user_name', 'user_email', 'product_name']}
        emptyText="No matches."
        emptyNoDataText="No favourites yet."
        headers={
          <tr>
            <th>User</th>
            <th>Email</th>
            <th>Product</th>
            <th>Created</th>
          </tr>
        }
        renderRow={(f) => (
          <tr key={f.id}>
            <td>{f.user_name}</td>
            <td>{f.user_email}</td>
            <td>{f.product_name}</td>
            <td>{new Date(f.created_at).toLocaleString()}</td>
          </tr>
        )}
      />
    </div>
  );
}

function renderCart({ cart_items, searchQuery, onSearch }) {
  return (
    <div className="admin-view">
      <SectionHead title="Cart Items" subtitle="Products currently in customer carts." />
      <DataTable
        data={cart_items}
        searchQuery={searchQuery}
        onSearch={onSearch}
        searchPlaceholder="Search cart items…"
        searchFields={['user_name', 'user_email', 'product_name', 'quantity', 'price']}
        emptyText="No matches."
        emptyNoDataText="No items in carts."
        headers={
          <tr>
            <th>User</th>
            <th>Email</th>
            <th>Product</th>
            <th>Qty</th>
            <th>Price</th>
            <th>Created</th>
          </tr>
        }
        renderRow={(i) => (
          <tr key={i.id}>
            <td>{i.user_name}</td>
            <td>{i.user_email}</td>
            <td>{i.product_name}</td>
            <td>{i.quantity}</td>
            <td>Rs. {parseFloat(i.price).toFixed(2)}</td>
            <td>{new Date(i.created_at).toLocaleString()}</td>
          </tr>
        )}
      />
    </div>
  );
}

// ================= Messages (doctor consultations) =================

function MessagesView({ messages, isDoctor, onReply, searchQuery, onSearch }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Group messages into conversations keyed by user + doctor
  const conversations = {};
  messages.forEach((m) => {
    const key = `${m.user_id}-${m.doctor_id}`;
    if (!conversations[key]) {
      conversations[key] = {
        key,
        user_id: m.user_id,
        doctor_id: m.doctor_id,
        user_name: m.user_name,
        user_email: m.user_email,
        doctor_name: m.doctor_name,
        items: [],
      };
    }
    conversations[key].items.push(m);
  });
  let convList = Object.values(conversations).sort((a, b) => {
    const at = new Date(a.items[a.items.length - 1].created_at).getTime();
    const bt = new Date(b.items[b.items.length - 1].created_at).getTime();
    return bt - at;
  });
  const q = (searchQuery || '').trim().toLowerCase();
  if (q) {
    convList = convList.filter(
      (c) =>
        String(c.user_name || '').toLowerCase().includes(q) ||
        String(c.user_email || '').toLowerCase().includes(q) ||
        String(c.doctor_name || '').toLowerCase().includes(q) ||
        c.items.some((m) => String(m.body || '').toLowerCase().includes(q))
    );
  }

  useEffect(() => {
    setPage(1);
  }, [searchQuery, pageSize]);

  const total = convList.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIdx = total === 0 ? 0 : (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, total);
  const pageConvs = convList.slice(startIdx, endIdx);

  return (
    <div className="admin-view">
      <SectionHead
        title="Messages"
        subtitle={isDoctor ? 'Patient messages addressed to you. Reply and your patient will see it.' : 'All doctor consultations across the platform.'}
      />
      <div className="admin-dt-toolbar">
        <label className="admin-dt-length">
          Show{' '}
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            aria-label="Conversations per page"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>{' '}
          entries
        </label>
        <TableSearch
          value={searchQuery}
          onChange={onSearch}
          placeholder="Search messages by patient, doctor, text…"
        />
      </div>
      {pageConvs.length === 0 ? (
        <div className="admin-table-card">
          <div className="admin-empty">{q ? 'No matching conversations.' : 'No messages yet.'}</div>
        </div>
      ) : (
        <div className="admin-convo-list">
          {pageConvs.map((c) => (
            <ConversationCard key={c.key} convo={c} isDoctor={isDoctor} onReply={onReply} />
          ))}
        </div>
      )}
      <div className="admin-dt-footer">
        <div className="admin-dt-info">
          {total === 0
            ? 'Showing 0 entries'
            : `Showing ${startIdx + 1} to ${endIdx} of ${total} entries`}
        </div>
        <div className="admin-dt-paginate" role="navigation" aria-label="Messages pagination">
          <button type="button" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
            Previous
          </button>
          <span className="admin-dt-page-label">
            Page {safePage} of {totalPages}
          </span>
          <button
            type="button"
            disabled={safePage >= totalPages}
            onClick={() => setPage(safePage + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function ConversationCard({ convo, isDoctor, onReply }) {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const submitReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    const last = convo.items[convo.items.length - 1];
    const ok = await onReply(last, reply);
    setSending(false);
    if (ok) setReply('');
  };

  return (
    <div className="admin-convo-card">
      <div className="admin-convo-head">
        <div>
          <span className="admin-convo-title">{convo.user_name}</span>
          <span className="admin-convo-sub">{convo.user_email}</span>
        </div>
        <span className="admin-convo-doctor">
          <FontAwesomeIcon icon={faUserDoctor} /> {convo.doctor_name}
        </span>
      </div>
      <div className="admin-convo-thread">
        {convo.items.map((m) => (
          <div
            key={m.id}
            className={`admin-convo-bubble ${m.sender_role === 'doctor' ? 'doctor' : 'user'}`}
          >
            <span className="admin-convo-sender">
              {m.sender_role === 'doctor' ? convo.doctor_name : convo.user_name}
            </span>
            <span className="admin-convo-body">{m.body}</span>
            <span className="admin-convo-time">{new Date(m.created_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
      {isDoctor && (
        <div className="admin-convo-reply">
          <textarea
            rows="2"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write a reply to the patient..."
          />
          <button
            type="button"
            className="admin-btn primary"
            onClick={submitReply}
            disabled={sending || !reply.trim()}
          >
            <FontAwesomeIcon icon={faPaperPlane} /> {sending ? 'Sending…' : 'Reply'}
          </button>
        </div>
      )}
    </div>
  );
}

// ================= Charts (pure SVG, no deps) =================

function BarChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="admin-bar-chart">
      {data.map((d) => (
        <div key={d.name} className="admin-bar-col">
          <div className="admin-bar-track">
            <div
              className="admin-bar-fill"
              style={{ height: `${(d.value / max) * 100}%`, background: d.color }}
            >
              <span className="admin-bar-value">{d.value}</span>
            </div>
          </div>
          <span className="admin-bar-label">{d.name}</span>
        </div>
      ))}
    </div>
  );
}

function DonutChart({ data, total }) {
  const size = 180;
  const stroke = 26;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const sum = data.reduce((s, d) => s + d.value, 0) || 1;
  let offset = 0;

  return (
    <div className="admin-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#eef2f7" strokeWidth={stroke} />
          {data.map((d) => {
            const len = (d.value / sum) * circumference;
            const dash = `${len} ${circumference - len}`;
            const el = (
              <circle
                key={d.name}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={d.color}
                strokeWidth={stroke}
                strokeDasharray={dash}
                strokeDashoffset={-offset}
              />
            );
            offset += len;
            return el;
          })}
        </g>
        <text x="50%" y="47%" textAnchor="middle" className="admin-donut-total">{total}</text>
        <text x="50%" y="60%" textAnchor="middle" className="admin-donut-caption">Orders</text>
      </svg>
      <div className="admin-donut-legend">
        {data.map((d) => (
          <div key={d.name} className="admin-legend-row">
            <span className="admin-legend-dot" style={{ background: d.color }} />
            <span className="admin-legend-name">{d.name}</span>
            <span className="admin-legend-val">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminDashboard;
